import { useMutationGetTarif } from '@workspace/core-ts/api';
import { useGlobalForm } from '@workspace/core-ts/contexts';
import { trackPromoAttempt } from '@workspace/utils';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FieldInput, Button } from '@workspace/core-ts/components';
import DOMPurify from 'dompurify';

interface CodePromoProps {
    /**
     * Callback appelé après la validation du code promo
     */
    onCodePromoValidated?: (data: any) => void;
    /**
     * Textes personnalisables
     */
    labels?: {
        title?: string;
        titleMobile?: string;
        placeholder?: string;
        buttonText?: string;
        errorMessage?: string;
        successMessage?: string;
    };
    /**
     * Classes CSS personnalisables
     */
    className?: string;
}

export function CodePromo({
    onCodePromoValidated,
    labels = {},
    className = '',
}: CodePromoProps) {
    const methods = useFormContext();
    const { meta, actions, others } = useGlobalForm();
    const mutationGetTarif = useMutationGetTarif(meta.token);
    const apporteur = others?.apporteur?.codeBelair;

    const [validationStatus, setValidationStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const defaultLabels = {
        title: 'Vous avez un code promo ?',
        titleMobile: 'Vous avez<br />un code promo ?',
        placeholder: 'Code promo',
        buttonText: 'Valider',
        errorMessage: "Le code promo n'est pas valide.",
        successMessage: 'Le code promo a bien été appliqué.',
        ...labels,
    };

    const handleValidateCodePromo = async () => {
        try {
            setValidationStatus('idle');

            // Récupérer la valeur du code promo directement
            const codePromoValue = methods.getValues('DemandeTarif.CodePromo.CodePromo' as any);

            console.log('=== CODE PROMO DEBUG ===');
            console.log('Raw CodePromo value:', codePromoValue);

            if (!codePromoValue || codePromoValue.trim() === '') {
                console.error('Code promo vide ou invalide');
                setValidationStatus('error');
                return;
            }

            // Normaliser le code promo (trim et uppercase)
            const normalizedCode = codePromoValue.trim().toUpperCase();
            methods.setValue('DemandeTarif.CodePromo.CodePromo' as any, normalizedCode, { shouldValidate: true });

            console.log('Normalized code:', normalizedCode);

            // Récupérer toutes les données du formulaire APRÈS normalisation
            const formData = methods.getValues();
            console.log('Form data being sent:', formData);
            console.log('CodePromo in formData:', formData?.DemandeTarif?.CodePromo?.CodePromo);

            // Appeler get_tarif pour récupérer le tarif avec validation du code promo
            const response = await mutationGetTarif.mutateAsync(formData);

            console.log('API Response:', response);
            console.log('Tarif:', response?.data?.Tarif);
            console.log('CodePromo in response:', response?.data?.Tarif?.CodePromo);

            if (response?.data?.Tarif) {
                const tarif = response.data.Tarif;
                const codePromo = tarif.CodePromo;

                // Vérifier si le code promo est valide
                if (codePromo?.Eligible) {
                    setValidationStatus('success');
                    trackPromoAttempt(normalizedCode, true, apporteur);

                    // Mettre à jour le tarif avec les nouvelles données
                    methods.setValue('Tarif', tarif, { shouldDirty: true });

                    // Forcer la mise à jour du DemandeTarif si présent dans la réponse
                    if (response.data.DemandeTarif) {
                        methods.setValue('DemandeTarif', {
                            ...methods.getValues('DemandeTarif'),
                            ...response.data.DemandeTarif,
                        }, { shouldDirty: true });
                    }

                    // Mettre à jour le state global avec les nouvelles données
                    actions.saveData(response.data);

                    // Callback optionnel
                    if (onCodePromoValidated) {
                        onCodePromoValidated(response.data);
                    }
                } else {
                    setValidationStatus('error');
                    trackPromoAttempt(normalizedCode, false, apporteur);
                }
            } else {
                setValidationStatus('error');
            }
        } catch (error) {
            setValidationStatus('error');
            console.error('Erreur lors de la validation du code promo:', error);
        }
    };

    return (
        <div className={`rounded-lg bg-card p-4 sm:p-6 w-full mt-6 ${className}`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                {/* Titre */}
                <div className="sm:w-1/3">
                    <p
                        className="text-sm sm:text-base font-semibold text-foreground hidden sm:block"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(defaultLabels.titleMobile) }}
                    />
                    <p className="text-sm sm:text-base font-semibold text-foreground sm:hidden">
                        {defaultLabels.title}
                    </p>
                </div>

                {/* Champ et bouton */}
                <div className="flex-1 w-full">
                    <div className="flex items-start gap-3">
                        <div className="flex-1">
                            <FieldInput
                                name="DemandeTarif.CodePromo.CodePromo"
                                placeholder={defaultLabels.placeholder}
                                label=""
                                required={false}
                            />
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleValidateCodePromo}
                            disabled={mutationGetTarif.isPending}
                            className="mt-0 h-[50px] px-4 sm:px-6"
                        >
                            {mutationGetTarif.isPending ? (
                                <span className="flex items-center gap-2">
                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </span>
                            ) : (
                                defaultLabels.buttonText
                            )}
                        </Button>
                    </div>

                    {/* Messages de feedback */}
                    {validationStatus === 'error' && (
                        <p className="text-red-600 text-xs sm:text-sm mt-2">{defaultLabels.errorMessage}</p>
                    )}
                    {validationStatus === 'success' && (
                        <p className="text-green-600 text-xs sm:text-sm mt-2">{defaultLabels.successMessage}</p>
                    )}
                </div>
            </div>
        </div>
    );
}
